export const SITE_NAME = 'AutoPlace';
export const SITE_URL = 'https://autoplace.al';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;

export function buildSlug(make: string, model: string, year: number | string, price: number | string, city?: string): string {
  const parts = [make, model, String(year), String(price), city].filter(Boolean);
  return parts
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9À-ɏ]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 75);
}

export function buildListingTitle(make: string, model: string, year: number | string, price: number | string): string {
  return `${make} ${model} ${year} | €${Number(price).toLocaleString('de-DE')} | AutoPlace Albania`;
}

export function buildListingDescription(
  make: string, model: string, year: number | string,
  mileage: number | string, fuelType: string | undefined,
  price: number | string, city?: string,
): string {
  const fuel = fuelType ? `, ${fuelType.replace(/_/g, ' ')}` : '';
  const loc = city ? ` në ${city}` : '';
  return `${make} ${model} ${year} në shitje në AutoPlace${loc}. ${Number(mileage).toLocaleString()} km${fuel}, dokumenta të plota. Çmim €${Number(price).toLocaleString('de-DE')}. Inspektim falas. Blej tani!`;
}

export function buildVehicleJsonLd(listing: {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuelType?: string;
  transmission?: string;
  bodyType?: string;
  color?: string;
  description?: string;
  images?: string[];
  city?: string;
  slug?: string;
}): object {
  const slug = listing.slug || buildSlug(listing.make, listing.model, listing.year, listing.price, listing.city);
  const url = `${SITE_URL}/listing/${listing.id}/${slug}`;
  const mainImage = listing.images?.[0] ?? DEFAULT_OG_IMAGE;

  return {
    '@context': 'https://schema.org/',
    '@type': 'Vehicle',
    name: listing.title,
    description: listing.description || buildListingDescription(listing.make, listing.model, listing.year, listing.mileage, listing.fuelType, listing.price, listing.city),
    image: listing.images?.slice(0, 5) ?? [mainImage],
    url,
    manufacturer: listing.make,
    model: listing.model,
    modelDate: String(listing.year),
    ...(listing.fuelType && { fuelType: listing.fuelType.replace(/_/g, ' ') }),
    ...(listing.transmission && { vehicleTransmission: listing.transmission }),
    ...(listing.bodyType && { bodyType: listing.bodyType }),
    ...(listing.color && { color: listing.color }),
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      unitCode: 'KMT',
      value: String(listing.mileage),
    },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'EUR',
      price: String(listing.price),
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
      },
    },
  };
}

export function buildBreadcrumbJsonLd(crumbs: { name: string; url: string }[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

export function buildOrganizationJsonLd(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon_io/android-chrome-512x512.png`,
    sameAs: [
      'https://www.facebook.com/autoplace.al',
      'https://www.instagram.com/autoplace.al',
    ],
  };
}

export function buildWebSiteJsonLd(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/mysearch?searchQuery={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}
